/**
 * English translations
 */
export const en = {
  errors: {
    roomNoEmail: "This room does not have a configured email address. Please contact your administrator.",
    roomAlreadyBooked: "This room is already booked for this time slot. Try another room.",
    bookingFailed: "Unable to add the room to the event",
    networkError: "Check your internet connection and try again. If the problem persists, try reconnecting.",
    authError: "Please sign in again.",
    authRequired: "Authentication required",
    authFailed: "Authentication failed. Please try again.",
    accountNotFound: "No Deskare account is associated to the email {email}. Please contact your administrator.",
    userCancelled: "Sign-in was cancelled. Please try again when you are ready.",
    accountNotSupported: "This account type is not supported. Please sign in with a Microsoft 365 work or school account.",
    popupBlocked: "The sign-in window was blocked by your browser. Allow popups for this add-in, then try again.",
    consentRequired: "Administrator consent is required. Please ask your administrator to approve this add-in.",
    apiNotAvailable: "The Microsoft identity service is not available in this version of Outlook. Please update Outlook and retry.",
    validationError: "Please check your data and try again.",
    unknownError: "An unexpected error occurred.",
    outlookContextUnavailable: "Office context is not available",
    outlookNoAppointment: "No active appointment",
    outlookCannotUpdateLocation: "Unable to update location",
    outlookCannotAddAttendee: "Unable to add attendee",
    outlookCannotStoreData: "Unable to save data",
  },
  success: {
    roomAdded: "The room has been added to your event.",
  },
  ui: {
    // Buttons
    addRoom: "Add Room",
    addingRoom: "Adding room...",
    addAnotherRoom: "Add Another Room",
    done: "Done",
    close: "Close",
    viewOtherRooms: "View other rooms",
    searchRooms: "Search Rooms",
    reconnect: "Reconnect",

    // Status badges
    roomFree: "Free",
    roomBooked: "Booked",

    // Dialog
    roomAddedTitle: "Room Added!",
    roomAddedDescription: "The room has been added to your event and set as the location.",

    // Loading states
    loadingAppointment: "Loading appointment details...",

    // Filters
    filters: "Filters",
    minCapacity: "Minimum Capacity",
    equipment: "Equipment",

    // Empty states
    noRoomsFound: "No rooms found",
    adjustFilters: "Try adjusting your filters",
    roomFound: "room found",
    roomsFound: "rooms found",

    // Room details
    capacity: "Capacity",
    attendee: "attendee",
    attendees: "attendees",
    // New UI
    tabsRooms: "Rooms",
    tabsInsights: "Meeting insights",
    tabsShortlist: "Shortlist",
    meetingSummary: "Meeting overview",
    insightsTitle: "Prepare the meeting",
    recommendedRooms: "Curated rooms for this slot",
    timeWindow: "Time window",
    noShortlist: "Start a search to curate rooms for this meeting",

    // New UI labels for redesign
    recommendedForYou: "Recommended for You",
    refineSearch: "Refine Search",
    people: "people",
    allRooms: "All Rooms",
    loadingRooms: "Loading rooms...",
    tryAdjustFilters: "Try adjusting the filters",
    noSubject: "No Subject",
    organizer: "Organizer",
    you: "You",
    location: "Location",
    more: "more",
    confirmed: "Confirmed",
    changeRoom: "Change Room",
    cancelReservation: "Cancel Reservation",
    loadingReservations: "Loading reservations...",
    errorLoadingReservations: "Error loading reservations",
    currentMeetingReservation: "Current Meeting Reservation",
    noReservations: "No Reservations",
    noReservationsDescription: "You have no room booked for this meeting",
    tabsBook: "Book",
    tabsReservations: "My Reservations",
    cancel: "Cancel",
    bookingMethodTitle: "Choose Booking Method",
    bookingMethodDescription: "This room belongs to a different domain. How would you like to book it?",
    bookingMethodAttendee: "Add as Attendee",
    bookingMethodAttendeeDescription: "Add the room as a meeting participant via Outlook",
    bookingMethodDeskare: "Book via Deskare",
    bookingMethodDeskareDescription: "Reserve the room directly through Deskare",
    allOfficesLabel: "All offices",
    office: "Office",
    signOut: "Sign Out",
    signIn: "Sign In",
    signedOutMessage: "You have been signed out.",
  },
  errorContext: {
    conflict: "This room is already booked for this time slot. Try another room.",
    network: "Check your internet connection and try again. If the problem persists, try reconnecting.",
    auth: "Please sign in again.",
    validation: "Please check your data and try again.",
  },
};

// Define the structure type based on the shape, not literal values
export interface TranslationStrings {
  errors: {
    roomNoEmail: string;
    roomAlreadyBooked: string;
    bookingFailed: string;
    networkError: string;
    authError: string;
    authRequired: string;
    authFailed: string;
    accountNotFound: string;
    userCancelled: string;
    accountNotSupported: string;
    popupBlocked: string;
    consentRequired: string;
    apiNotAvailable: string;
    validationError: string;
    unknownError: string;
    outlookContextUnavailable: string;
    outlookNoAppointment: string;
    outlookCannotUpdateLocation: string;
    outlookCannotAddAttendee: string;
    outlookCannotStoreData: string;
  };
  success: {
    roomAdded: string;
  };
  ui: {
    addRoom: string;
    addingRoom: string;
    addAnotherRoom: string;
    done: string;
    close: string;
    viewOtherRooms: string;
    searchRooms: string;
    reconnect: string;
    roomFree: string;
    roomBooked: string;
    roomAddedTitle: string;
    roomAddedDescription: string;
    loadingAppointment: string;
    filters: string;
    minCapacity: string;
    equipment: string;
    noRoomsFound: string;
    adjustFilters: string;
    roomFound: string;
    roomsFound: string;
    capacity: string;
    attendee: string;
    attendees: string;
    tabsRooms: string;
    tabsInsights: string;
    tabsShortlist: string;
    meetingSummary: string;
    insightsTitle: string;
    recommendedRooms: string;
    timeWindow: string;
    noShortlist: string;
    recommendedForYou: string;
    refineSearch: string;
    people: string;
    allRooms: string;
    loadingRooms: string;
    tryAdjustFilters: string;
    noSubject: string;
    organizer: string;
    you: string;
    location: string;
    more: string;
    confirmed: string;
    changeRoom: string;
    cancelReservation: string;
    loadingReservations: string;
    errorLoadingReservations: string;
    currentMeetingReservation: string;
    noReservations: string;
    noReservationsDescription: string;
    tabsBook: string;
    tabsReservations: string;
    cancel: string;
    bookingMethodTitle: string;
    bookingMethodDescription: string;
    bookingMethodAttendee: string;
    bookingMethodAttendeeDescription: string;
    bookingMethodDeskare: string;
    bookingMethodDeskareDescription: string;
    allOfficesLabel: string;
    office: string;
    signOut: string;
    signIn: string;
    signedOutMessage: string;
  };
  errorContext: {
    conflict: string;
    network: string;
    auth: string;
    validation: string;
  };
}

export type Translations = TranslationStrings;
